import { DEVENV_DOMAIN_SUFFIX } from 'shared/utils/devenv';

export const DEFAULT_ROOT_DOMAIN = 'norm.ai';
export const DEFAULT_SERVICE_PREFIXES = ['app', 'api', 'storage'];

/**
 * Extracts root domain for cookie sharing.
 * e.g., 'bwater.app.norm.ai' -> '.norm.ai'
 */
export function getRootDomain(hostname: string): string {
  const parts = hostname.split('.');

  // Treat localhost as a TLD (like 'ai') for local development
  if (parts[parts.length - 1] === 'localhost') {
    return parts.length >= 2 ? `.${parts.slice(-2).join('.')}` : '.localhost';
  }

  if (parts.length >= 2) {
    return `.${parts.slice(-2).join('.')}`;
  }
  return `.${hostname}`;
}

/** Throws in production if ALLOWED_ROOT_DOMAIN is not configured. */
export function getAllowedRootDomain(): string {
  const allowedRootDomain =
    process.env.ALLOWED_ROOT_DOMAIN ||
    process.env.NEXT_PUBLIC_ALLOWED_ROOT_DOMAIN;

  if (allowedRootDomain) {
    return allowedRootDomain;
  }

  if (process.env.NODE_ENV === 'production') {
    throw new Error(
      'Missing required configuration: ALLOWED_ROOT_DOMAIN or ' +
        'NEXT_PUBLIC_ALLOWED_ROOT_DOMAIN must be set in production.',
    );
  }

  console.warn(
    `[hostname-utils] ALLOWED_ROOT_DOMAIN not configured. ` +
      `Falling back to '${DEFAULT_ROOT_DOMAIN}' for local development.`,
  );
  return DEFAULT_ROOT_DOMAIN;
}

export function getAllowedServicePrefixes(): string[] {
  const envPrefixes = process.env.ALLOWED_SERVICE_PREFIXES;
  if (envPrefixes) {
    return envPrefixes.split(',').map((p) => p.trim().toLowerCase());
  }
  return DEFAULT_SERVICE_PREFIXES;
}

/** RFC 1123: alphanumeric with hyphens, no leading/trailing hyphens, max 63 chars. */
export function isValidSubdomainComponent(component: string): boolean {
  if (!component || component.length > 63) return false;
  return /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/i.test(component);
}

/**
 * Validates hostname against allowed patterns to prevent redirect attacks.
 * Valid: {ROOT}, {SERVICE}.{ROOT}, {VANITY}.{SERVICE}.{ROOT}
 * e.g., norm.ai, app.norm.ai, bwater.app.norm.ai
 *
 * In development mode, localhost is automatically allowed for convenience.
 */
export function isAllowedHost(hostname: string): boolean {
  const hasExplicitConfig =
    process.env.ALLOWED_ROOT_DOMAIN ||
    process.env.NEXT_PUBLIC_ALLOWED_ROOT_DOMAIN;

  // Allow plain localhost in development when no explicit config is set
  if (
    hostname === 'localhost' &&
    process.env.NODE_ENV !== 'production' &&
    !hasExplicitConfig
  ) {
    return true;
  }

  const allowedRootDomain = getAllowedRootDomain();
  const allowedServices = getAllowedServicePrefixes();

  if (hostname === allowedRootDomain) {
    return true;
  }

  if (!hostname.endsWith(`.${allowedRootDomain}`)) {
    return false;
  }

  const prefix = hostname.slice(0, -(allowedRootDomain.length + 1));
  const parts = prefix.split('.');

  // localhost special handling: allow norm.localhost, app.norm.localhost, etc.
  if (allowedRootDomain === 'localhost') {
    if (parts.length === 1 && parts[0] === 'norm') {
      return true;
    }
    if (parts.length === 2 && parts[1] === 'norm') {
      return allowedServices.includes(parts[0].toLowerCase());
    }
    if (parts.length === 3 && parts[2] === 'norm') {
      const [vanity, service] = parts;
      return (
        allowedServices.includes(service.toLowerCase()) &&
        isValidSubdomainComponent(vanity)
      );
    }
    return false;
  }

  // devenv special handling: we use subdomains within DEVENV_DOMAIN_SUFFIX - allow all
  if (allowedRootDomain === DEVENV_DOMAIN_SUFFIX) {
    return true;
  }

  // app.norm.ai
  if (parts.length === 1) {
    return allowedServices.includes(parts[0].toLowerCase());
  }

  // bwater.app.norm.ai
  if (parts.length === 2) {
    const [vanity, service] = parts;
    return (
      allowedServices.includes(service.toLowerCase()) &&
      isValidSubdomainComponent(vanity)
    );
  }

  // Reject deep subdomains like evil.bwater.app.norm.ai
  return false;
}
